/**
 * Serviço de cache offline usando IndexedDB
 * Armazena produtos e categorias para uso offline
 */

import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'garcom_cache';
const DB_VERSION = 3; // Incrementado para adicionar STORE_OFFLINE_QUEUE
const STORE_PRODUCTS = 'produtos';
const STORE_CATEGORIES = 'categorias';
const STORE_GENERAL = 'general'; // Store genérica para outros dados
const STORE_OFFLINE_QUEUE = 'offline_queue'; // Store para fila de sincronização offline
const CACHE_DURATION = 60 * 60 * 1000; // 1 hora em milissegundos

// Cache em memória como fallback
const memoryCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

interface CacheData<T> {
  data: T;
  timestamp: number;
  ttl?: number; // TTL específico para este item
}

class CacheService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Erro ao abrir IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Criar stores se não existirem
        if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
          db.createObjectStore(STORE_PRODUCTS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_CATEGORIES)) {
          db.createObjectStore(STORE_CATEGORIES, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_GENERAL)) {
          db.createObjectStore(STORE_GENERAL, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_OFFLINE_QUEUE)) {
          db.createObjectStore(STORE_OFFLINE_QUEUE, { keyPath: 'id' });
        }
      };
    });
  }

  private async getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Database não inicializada');
    }
    return this.db.transaction([storeName], mode).objectStore(storeName);
  }

  async saveProducts(produtos: any[]): Promise<void> {
    try {
      const store = await this.getStore(STORE_PRODUCTS, 'readwrite');
      const cacheData: CacheData<any[]> = {
        data: produtos,
        timestamp: Date.now()
      };

      // Limpar dados antigos
      await store.clear();

      // Salvar novos dados
      await store.put({ id: 'all', ...cacheData });
    } catch (error) {
      console.error('Erro ao salvar produtos no cache:', error);
    }
  }

  async getProducts(): Promise<any[] | null> {
    try {
      const store = await this.getStore(STORE_PRODUCTS);
      const request = store.get('all');

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const result = request.result as CacheData<any[]> | undefined;
          if (!result) {
            resolve(null);
            return;
          }

          // Verificar se cache ainda é válido
          const age = Date.now() - result.timestamp;
          if (age > CACHE_DURATION) {
            resolve(null);
            return;
          }

          resolve(result.data);
        };

        request.onerror = () => {
          resolve(null);
        };
      });
    } catch (error) {
      console.error('Erro ao recuperar produtos do cache:', error);
      return null;
    }
  }

  async saveCategories(categorias: any[]): Promise<void> {
    try {
      const store = await this.getStore(STORE_CATEGORIES, 'readwrite');
      const cacheData: CacheData<any[]> = {
        data: categorias,
        timestamp: Date.now()
      };

      await store.clear();
      await store.put({ id: 'all', ...cacheData });
    } catch (error) {
      console.error('Erro ao salvar categorias no cache:', error);
    }
  }

  async getCategories(): Promise<any[] | null> {
    try {
      const store = await this.getStore(STORE_CATEGORIES);
      const request = store.get('all');

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const result = request.result as CacheData<any[]> | undefined;
          if (!result) {
            resolve(null);
            return;
          }

          const age = Date.now() - result.timestamp;
          if (age > CACHE_DURATION) {
            resolve(null);
            return;
          }

          resolve(result.data);
        };

        request.onerror = () => {
          resolve(null);
        };
      });
    } catch (error) {
      console.error('Erro ao recuperar categorias do cache:', error);
      return null;
    }
  }

  async clearCache(): Promise<void> {
    try {
      const productsStore = await this.getStore(STORE_PRODUCTS, 'readwrite');
      const categoriesStore = await this.getStore(STORE_CATEGORIES, 'readwrite');
      
      await productsStore.clear();
      await categoriesStore.clear();
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  // Métodos genéricos para cache geral (compatibilidade com useCachedData)
  async save(key: string, data: any, ttl: number = CACHE_DURATION): Promise<void> {
    try {
      // Salvar no IndexedDB
      const store = await this.getStore(STORE_GENERAL, 'readwrite');
      const cacheData: CacheData<any> = {
        data,
        timestamp: Date.now(),
        ttl
      };
      await store.put({ id: key, ...cacheData });
      
      // Também salvar em memória como fallback
      memoryCache.set(key, { data, timestamp: Date.now(), ttl });
    } catch (error) {
      console.error('Erro ao salvar no cache:', error);
      // Fallback para memória apenas
      memoryCache.set(key, { data, timestamp: Date.now(), ttl });
    }
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      // Tentar IndexedDB primeiro
      const store = await this.getStore(STORE_GENERAL);
      const request = store.get(key);

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const result = request.result as CacheData<T> | undefined;
          if (!result) {
            // Tentar memória como fallback
            const memData = memoryCache.get(key);
            if (memData && (Date.now() - memData.timestamp) < memData.ttl) {
              resolve(memData.data as T);
              return;
            }
            resolve(null);
            return;
          }

          // Verificar se cache ainda é válido (usar TTL específico ou CACHE_DURATION como padrão)
          const itemTTL = result.ttl || CACHE_DURATION;
          const age = Date.now() - result.timestamp;
          if (age > itemTTL) {
            resolve(null);
            return;
          }

          resolve(result.data);
        };

        request.onerror = () => {
          // Fallback para memória
          const memData = memoryCache.get(key);
          if (memData && (Date.now() - memData.timestamp) < memData.ttl) {
            resolve(memData.data as T);
            return;
          }
          resolve(null);
        };
      });
    } catch (error) {
      // Fallback para memória
      const memData = memoryCache.get(key);
      if (memData && (Date.now() - memData.timestamp) < memData.ttl) {
        return memData.data as T;
      }
      return null;
    }
  }

  async invalidate(key: string): Promise<void> {
    try {
      const store = await this.getStore(STORE_GENERAL, 'readwrite');
      await store.delete(key);
    } catch (error) {
      console.error('Erro ao invalidar cache:', error);
    }
    memoryCache.delete(key);
  }

  async invalidatePattern(pattern: string): Promise<void> {
    try {
      const regex = new RegExp(pattern);
      
      // Invalidar no IndexedDB
      const store = await this.getStore(STORE_GENERAL, 'readwrite');
      const request = store.openCursor();
      
      return new Promise((resolve) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            const key = cursor.key as string;
            if (regex.test(key)) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            // Invalidar no cache em memória
            for (const key of memoryCache.keys()) {
              if (regex.test(key)) {
                memoryCache.delete(key);
              }
            }
            resolve();
          }
        };
        
        request.onerror = () => {
          // Fallback: invalidar apenas no cache em memória
          for (const key of memoryCache.keys()) {
            if (regex.test(key)) {
              memoryCache.delete(key);
            }
          }
          resolve();
        };
      });
    } catch (error) {
      console.error('Erro ao invalidar padrão de cache:', error);
      // Fallback: invalidar apenas no cache em memória
      const regex = new RegExp(pattern);
      for (const key of memoryCache.keys()) {
        if (regex.test(key)) {
          memoryCache.delete(key);
        }
      }
    }
  }

  set(key: string, data: any, ttl: number = CACHE_DURATION): void {
    this.save(key, data, ttl).catch(console.error);
  }

  // --- Offline Sync Queue Methods ---

  async addToSyncQueue(actionType: string, url: string, payload: any): Promise<number> {
    try {
      const store = await this.getStore(STORE_OFFLINE_QUEUE, 'readwrite');
      const id = Date.now(); // Usar timestamp como ID
      const syncItem = {
        id,
        actionType,
        url,
        payload,
        timestamp: id
      };
      await store.put(syncItem);
      return id;
    } catch (error) {
      console.error('Erro ao adicionar na fila offline:', error);
      return 0;
    }
  }

  async getSyncQueue(): Promise<any[]> {
    try {
      const store = await this.getStore(STORE_OFFLINE_QUEUE);
      const request = store.getAll();

      return new Promise((resolve) => {
        request.onsuccess = () => {
          // Ordenar por timestamp
          const result = (request.result || []).sort((a: any, b: any) => a.timestamp - b.timestamp);
          resolve(result);
        };
        request.onerror = () => {
          resolve([]);
        };
      });
    } catch (error) {
      console.error('Erro ao recuperar fila offline:', error);
      return [];
    }
  }

  async removeFromSyncQueue(id: number): Promise<void> {
    try {
      const store = await this.getStore(STORE_OFFLINE_QUEUE, 'readwrite');
      await store.delete(id);
    } catch (error) {
      console.error('Erro ao remover da fila offline:', error);
    }
  }

  async clearSyncQueue(): Promise<void> {
    try {
      const store = await this.getStore(STORE_OFFLINE_QUEUE, 'readwrite');
      await store.clear();
    } catch (error) {
      console.error('Erro ao limpar fila offline:', error);
    }
  }
}

export const cacheService = new CacheService();

/**
 * Hook useCachedData - Compatibilidade com código existente
 * Cache genérico para qualquer tipo de dado
 */
export function useCachedData<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  ttl: number = 5 * 60 * 1000, // 5 minutos padrão
  dependencies: any[] = []
): { data: T | null; loading: boolean; error: Error | null; refetch: () => Promise<void> } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar cache primeiro
      const cachedData = await cacheService.get<T>(cacheKey);
      if (cachedData) {
        setData(cachedData);
        setLoading(false);
        return;
      }

      // Buscar dados
      const result = await fetchFn();
      
      // Salvar no cache
      await cacheService.save(cacheKey, result, ttl);
      
      setData(result);
    } catch (err) {
      const error = err as Error;
      setError(error);
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, ttl, ...dependencies]);

  const refetch = useCallback(async () => {
    await cacheService.invalidate(cacheKey);
    await fetchData();
  }, [cacheKey, fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Inicializar cache ao carregar
if (typeof window !== 'undefined') {
  cacheService.init().catch(console.error);
}
